import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '0', 10);
  const comic = comics.find((c) => c.id === comicId);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto bg-background-200 rounded-full flex items-center justify-center mb-6">
              <i className="ri-file-unknow-line text-4xl text-foreground-400" />
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-3">未找到该漫画</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-white rounded-full font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2" />
              回到首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const tagMap = categories.reduce<Record<string, string>>((acc, cat) => {
    acc[cat.slug] = cat.name;
    return acc;
  }, {});

  const categoryMap = categories.reduce<Record<string, string>>((acc, cat) => {
    acc[cat.slug] = cat.name;
    return acc;
  }, {});

  const categoryName = categoryMap[comic.category] || comic.category;

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ComicSeries',
    name: comic.title,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    description: comic.description,
    url: `https://xinxinmanhua.com/comic/${comic.id}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: '10',
      ratingCount: comic.popularity,
    },
    numberOfEpisodes: comic.chapters,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-8">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: categoryName, to: `/category/${comic.category}` },
                  { label: comic.title },
                ]}
              />
            </div>

            {/* Comic Detail */}
            <article className="bg-white rounded-xl border border-background-200 overflow-hidden">
              <div className="flex flex-col md:flex-row">
                {/* Cover */}
                <div className="w-full md:w-[320px] flex-shrink-0">
                  <div className="aspect-[3/4] md:h-full relative overflow-hidden bg-background-200">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - 新新漫画推荐`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${
                      comic.status === '连载中' ? 'bg-primary-500' : 'bg-secondary-500'
                    }`}>
                      {comic.status}
                    </span>
                    <Link
                      to={`/category/${comic.category}`}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-background-200 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      {categoryName}
                    </Link>
                    {comic.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/category/${tag}`}
                        className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-500 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                      >
                        {tagMap[tag] || tag}
                      </Link>
                    ))}
                  </div>

                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                    {comic.title}
                  </h1>
                  <p className="text-foreground-500 mb-6">
                    作者：<strong className="text-foreground-800">{comic.author}</strong>
                  </p>

                  <div className="flex items-center gap-6 mb-6 flex-wrap">
                    <div className="flex items-center gap-2">
                      <i className="ri-star-fill text-yellow-500 text-lg" />
                      <span className="text-xl font-bold text-foreground-900">{comic.rating}</span>
                      <span className="text-sm text-foreground-500">/ 10</span>
                    </div>
                    <div className="flex items-center gap-2 text-foreground-500 text-sm">
                      <i className="ri-eye-line" />
                      <span>{(comic.popularity / 10000).toFixed(1)} 万人气</span>
                    </div>
                    <div className="flex items-center gap-2 text-foreground-500 text-sm">
                      <i className="ri-book-open-line" />
                      <span>{comic.chapters} 话</span>
                    </div>
                  </div>

                  <div className="bg-background-50 rounded-lg p-4 mb-6">
                    <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                  </div>

                  <div className="flex items-center gap-4">
                    <button className="px-8 py-2.5 bg-primary-500 text-white rounded-full font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2">
                      <i className="ri-book-read-line" />
                      开始阅读
                    </button>
                    <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-400 hover:text-accent-500 hover:border-accent-300 transition-all cursor-pointer">
                      <i className="ri-heart-line text-lg" />
                    </button>
                    <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-400 hover:text-primary-500 hover:border-primary-300 transition-all cursor-pointer">
                      <i className="ri-bookmark-line text-lg" />
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {/* Chapter List */}
            <section className="mt-10">
              <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">章节列表</h2>
              <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-100">
                {Array.from({ length: Math.min(12, comic.chapters) }, (_, i) => comic.chapters - i).map((ch) => (
                  <div
                    key={ch}
                    className="flex items-center justify-between px-5 py-3 hover:bg-background-50 transition-colors cursor-pointer"
                  >
                    <span className="text-sm text-foreground-700">第 {ch} 话</span>
                    <span className="text-xs text-foreground-400">
                      {ch === comic.chapters ? '最新' : ''}
                    </span>
                  </div>
                ))}
                {comic.chapters > 12 && (
                  <div className="px-5 py-3 text-center text-sm text-primary-500 hover:text-primary-600 cursor-pointer font-medium">
                    查看全部 {comic.chapters} 话
                  </div>
                )}
              </div>
            </section>

            {/* Related Comics */}
            {relatedComics.length > 0 && (
              <section className="mt-14">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-foreground-900 font-heading">相关推荐</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5" data-product-shop>
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} {...c} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}